import { formatDistanceStrict, formatDistanceToNow } from 'date-fns';
import { CalendarClock } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import type { LaggingResource } from '@/types/connectors';

interface Props { resources?: LaggingResource[] | null; }

/** #2011 — a resource can stop returning rows while its run keeps reporting
 *  `partial`, and nothing outside one execution row said so. This lists the
 *  config's resources whose newest rows are more than a day behind the newest
 *  rows of their peers, so the gap is visible on the connector page itself. */
export function LaggingResourcesBanner({ resources }: Props) {
  if (!resources?.length) return null;
  return (
    <Alert variant="warning" data-testid="lagging-resources-banner">
      <CalendarClock className="h-4 w-4" aria-hidden />
      <AlertTitle>
        {resources.length === 1 ? '1 resource is' : `${resources.length} resources are`} behind
        the rest of this connector
      </AlertTitle>
      <AlertDescription>
        <p className="mb-1.5 text-foreground">
          The other resources have returned rows more recently. Check Activity for the runs that
          left these out.
        </p>
        <ul className="space-y-1">
          {resources.map((r) => (
            <li key={r.resource}>
              <span className="font-semibold text-foreground">{r.resource}</span>
              {' — last returned rows '}
              {formatDistanceToNow(new Date(r.last_rows_at), { addSuffix: true })}
              {', '}
              {formatDistanceStrict(new Date(r.last_rows_at), new Date(r.peer_last_rows_at))}
              {' before its peers'}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
